/**
 * Single-source-of-truth <head> assembly for the site's SEO sections.
 *
 * The SEO sections (PageSEO, CustomSEO, CustomSEOPLP, CustomSEOPDP) compute
 * the full head payload in their loaders; the route-level `head()` emits it
 * via TanStack Router and the section components render only JSON-LD.
 * Rendering the tags from the section components as well would hoist a
 * second copy of <title>/<meta name="description">/<meta name="robots">
 * into <head> next to the framework-generated one (DECO-5439/5440/5444).
 */

type HeadEntry = Record<string, string | undefined>

export interface SeoHeadPayload {
    title?: string
    meta?: HeadEntry[]
    link?: HeadEntry[]
    opengraph?: HeadEntry[]
    twitter?: HeadEntry[]
    nextPage?: string
    prevPage?: string
}

interface RouteHead {
    meta?: Array<Record<string, string>>
    links?: Array<Record<string, string>>
    scripts?: Array<Record<string, unknown>>
}

interface LoaderDataLike {
    resolvedSections?: Array<{ component: string; props?: Record<string, unknown> }>
}

const isSeoPayload = (props: unknown): props is SeoHeadPayload => {
    const p = props as SeoHeadPayload | null
    return Boolean(p && Array.isArray(p.meta) && Array.isArray(p.opengraph))
}

const clean = (entries: HeadEntry[] | undefined): Array<Record<string, string>> =>
    (entries ?? [])
        .map((entry) =>
            Object.fromEntries(
                Object.entries(entry).filter(([, v]) => typeof v === 'string' && v !== ''),
            ) as Record<string, string>
        )
        .filter((entry) => Object.keys(entry).length > 0)

/**
 * Build the route `head()` result from the last SEO section resolved on the
 * page. Falls back to the framework-generated head when the page has no SEO
 * section (or its loader bailed out, e.g. PLP without canonical).
 */
export function headFromSeoSections(
    loaderData: LoaderDataLike | null | undefined,
    base: RouteHead,
): RouteHead {
    let payload: SeoHeadPayload | undefined
    for (const section of loaderData?.resolvedSections ?? []) {
        if (isSeoPayload(section.props)) payload = section.props
    }
    if (!payload) return base

    const meta: Array<Record<string, string>> = []
    if (payload.title) meta.push({ title: payload.title })
    meta.push(
        ...clean(payload.meta),
        ...clean(payload.opengraph),
        ...clean(payload.twitter),
    )

    const links = clean(payload.link)
    if (payload.prevPage) links.push({ rel: 'prev', href: payload.prevPage })
    if (payload.nextPage) links.push({ rel: 'next', href: payload.nextPage })
    // Keep the framework's modulepreload hints for eager section chunks
    links.push(...(base.links ?? []).filter((l) => l.rel === 'modulepreload'))

    return { meta, links, scripts: base.scripts ?? [] }
}
